import { Epic } from "redux-observable";
import { from, Observable, of } from "rxjs";
import {
  filter,
  map,
  catchError,
  tap,
  mergeMap,
  toArray,
} from "rxjs/operators";
import { message } from "antd";
import { moveActions } from "./moveSlice";
import { FolderService } from "@/services/FolderService";
import { RootState } from "@/store/types";
import i18next from "i18next";
import { trackEvent, ItemAction } from "@/libs/analytics/analyticsEvents";

// Epic để xử lý move items
export const moveItemsEpic: Epic<any, any, RootState> = (action$, state$) =>
  action$.pipe(
    filter((action) => action.type === moveActions.moveItemsStart.type),
    mergeMap((action) => {
      const { fileIds, folderIds, destinationFolderId } = action.payload as {
        fileIds: string[];
        folderIds: string[];
        destinationFolderId: string;
      };

      const hideLoading = message.loading(i18next.t("move:moving_items"), 0);

      // Create array of observables for batch operations
      const operations: Observable<any>[] = [];

      // Add file move operation if there are files
      if (fileIds.length > 0) {
        operations.push(
          FolderService.Patch.moveMultipleFiles(fileIds, destinationFolderId)
        );
      }

      // Add folder move operation if there are folders
      if (folderIds.length > 0) {
        operations.push(
          FolderService.Patch.moveMultipleFolders(folderIds, destinationFolderId)
        );
      }

      // If no operations, return success immediately
      if (operations.length === 0) {
        hideLoading();
        return of(moveActions.moveItemsSuccess([]));
      }

      // Execute all operations in parallel
      return from(operations).pipe(
        mergeMap((obs) => obs),
        toArray(),
        tap(() => {
          hideLoading();
          const totalItems = fileIds.length + folderIds.length;
          message.success(i18next.t("move:items_moved_successfully", { count: totalItems, plural: totalItems > 1 ? "s" : "" }));
          
          fileIds.forEach((fileId) => {
            trackEvent({
              action: ItemAction.MOVE,
              item_id: fileId,
              item_type: "file",
              status: "success",
            });
          });
          
          folderIds.forEach((folderId) => {
            trackEvent({
              action: ItemAction.MOVE,
              item_id: folderId,
              item_type: "folder",
              status: "success",
            });
          });
        }),
        map((responses: any[]) => moveActions.moveItemsSuccess(responses)),
        catchError((error: any) => {
          hideLoading();
          const errorMessage = error?.message || "Failed to move items";
          message.error(errorMessage);
          
          // Track move failures
          fileIds.forEach((fileId) => {
            trackEvent({
              action: ItemAction.MOVE,
              item_id: fileId,
              item_type: "file",
              status: "fail",
              error_message: errorMessage || "Unknown error",
            });
          });
          
          folderIds.forEach((folderId) => {
            trackEvent({
              action: ItemAction.MOVE,
              item_id: folderId,
              item_type: "folder",
              status: "fail",
              error_message: errorMessage || "Unknown error",
            });
          });
          
          return of(moveActions.moveItemsFailure(errorMessage));
        })
      );
    })
  );

// Epic để cập nhật label (PUT /Label/{labelId})
// Note: This epic is kept for backward compatibility but is no longer used
// for the main move operation. Use moveItemsEpic instead.
export const updateLabelEpic: Epic<any, any, RootState> = (action$) =>
  action$.pipe(
    filter(moveActions.updateLabelRequest.match),
    mergeMap((action) => {
      const { labelId, parentId } = action.payload as {
        labelId: string;
        parentId: string;
      };

      const hideLoading = message.loading(i18next.t("move:updating_label"), 0);

      if (!labelId || !parentId) {
        hideLoading();
        const errMsg = "labelId and parentId are required";
        message.error(errMsg);
        return of(moveActions.updateLabelFailure(errMsg));
      }
      const body = {
        parentId,
      };

      return FolderService.Put.updateLabel(labelId, body).pipe(
        tap(() => hideLoading()),
        map((response: any) => {
          message.success(i18next.t("move:updated_label_successfully"));
          return moveActions.updateLabelSuccess(response);
        }),
        catchError((error: any) => {
          hideLoading();
          const errorMessage = error?.message || i18next.t("move:move_failed");
          message.error(errorMessage);
          return of(moveActions.updateLabelFailure(errorMessage));
        })
      );
    })
  );

// Export grouped epics for move feature
export const downloadFolderEpic: Epic<any, any, RootState> = (action$) =>
  action$.pipe(
    filter((action) => action.type === moveActions.downloadFolderRequest.type),
    mergeMap((action) => {
      const {
        labelId,
        folderName,
        includeSubfolders = true,
      } = action.payload as any;

      const zipFileName = `${folderName}-${Date.now()}`;

      const hideLoading = message.loading(
        i18next.t("move:downloading_folder", { folderName }),
        0
      );

      return FolderService.Post.downloadFolder(
        labelId,
        zipFileName,
        includeSubfolders
      ).pipe(
        tap(() => hideLoading()),
        map((response: any) => {
          // Check if response is a Blob (file zip)
          if (response instanceof Blob) {
            // Create download link for the blob
            const url = window.URL.createObjectURL(response);
            const link = document.createElement("a");
            link.href = url;
            link.download = `${zipFileName}.zip`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            // Clean up the URL object after download
            setTimeout(() => window.URL.revokeObjectURL(url), 100);

            message.success(i18next.t("move:downloaded_folder", { folderName }));
            
            trackEvent({
              action: ItemAction.DOWNLOAD,
              item_id: labelId,
              item_type: "folder",
              status: "success",
            });
            
            return moveActions.downloadFolderSuccess(zipFileName);
          } else {
            // Fallback: if response has url property
            message.error(i18next.t("move:invalid_response_format_folder", { folderName }));
            return moveActions.downloadFolderFailure(i18next.t("move:invalid_response_format"));
          }
        }),
        catchError((error: any) => {
          hideLoading();
          const errorMessage =
            error?.message || i18next.t("move:failed_download_folder", { folderName });
          message.error(errorMessage);
          
          trackEvent({
            action: ItemAction.DOWNLOAD,
            item_id: labelId,
            item_type: "folder",
            status: "fail",
            error_message: errorMessage || "Unknown error",
          });
          
          return of(moveActions.downloadFolderFailure(errorMessage));
        })
      );
    })
  );

export const MoveEpics = [moveItemsEpic, updateLabelEpic, downloadFolderEpic];
